import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { LectureLecturer } from '../../../../../api/types/lecturers/lectures/lecture-lecturer';
import { finishLectureOperation } from '../operations/finish-lecture-operation';
import appApi from '../../../../../api/app-api';
import { toast } from 'react-toastify';

interface MyLectureFinishDialogProps {
  visible: boolean;
  selectedLecture: LectureLecturer | null;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  onHide: () => void;
}

const getActualDurationMinutes = (lecture: LectureLecturer): number => {
  const plannedStart = new Date(lecture.startTime);
  const plannedEnd = new Date(lecture.endTime);

  const reportedStart = lecture.actualStartTime
    ? new Date(lecture.actualStartTime)
    : plannedStart;
  // When finishing now, treat "now" as the actual end if it isn't set yet.
  const reportedEnd = lecture.actualEndTime
    ? new Date(lecture.actualEndTime)
    : new Date();

  // Start = later of planned/actual; end = earlier of planned/actual.
  const effectiveStart = new Date(
    Math.max(plannedStart.getTime(), reportedStart.getTime()),
  );
  const effectiveEnd = new Date(
    Math.min(plannedEnd.getTime(), reportedEnd.getTime()),
  );

  return Math.max(
    0,
    Math.floor(
      (effectiveEnd.getTime() - effectiveStart.getTime()) / (60 * 1000),
    ),
  );
};

export const MyLectureFinishDialog: React.FC<MyLectureFinishDialogProps> = ({
  visible,
  selectedLecture,
  isLoading,
  setIsLoading,
  onHide,
}) => {
  const [lectureBreakMinutes, setLectureBreakMinutes] = useState<number>(0);
  const [plannedBreakMinutes, setPlannedBreakMinutes] = useState<number>(0);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);

  const actualDurationMinutes = selectedLecture
    ? getActualDurationMinutes(selectedLecture)
    : 0;
  const minBreakMinutes = Math.min(plannedBreakMinutes, actualDurationMinutes);
  const maxBreakMinutes = actualDurationMinutes;

  useEffect(() => {
    if (!visible || !selectedLecture) {
      setLectureBreakMinutes(0);
      setPlannedBreakMinutes(0);
      return;
    }

    const loadDefaultBreakMinutes = async () => {
      setIsLoadingDefault(true);
      try {
        const res = await appApi.getLectureDetails(
          selectedLecture.programId,
          selectedLecture.courseId,
          selectedLecture.id,
        );
        const defaultBreakMinutes =
          res.data.body?.lectureInformation?.lecture?.lectureBreakMinutes;
        const planned =
          defaultBreakMinutes !== null && defaultBreakMinutes !== undefined
            ? defaultBreakMinutes
            : 0;
        const duration = getActualDurationMinutes(selectedLecture);
        const requiredMin = Math.min(planned, duration);

        setPlannedBreakMinutes(planned);
        setLectureBreakMinutes(requiredMin);
      } catch (error) {
        console.error('Failed to load lecture break minutes default:', error);
        setPlannedBreakMinutes(0);
        setLectureBreakMinutes(0);
      } finally {
        setIsLoadingDefault(false);
      }
    };

    loadDefaultBreakMinutes();
  }, [visible, selectedLecture]);

  // Keep the input inside [min, max] when duration/planned bounds change.
  useEffect(() => {
    if (!visible || isLoadingDefault) {
      return;
    }
    setLectureBreakMinutes((prev) => {
      if (prev < minBreakMinutes) {
        return minBreakMinutes;
      }
      if (prev > maxBreakMinutes) {
        return maxBreakMinutes;
      }
      return prev;
    });
  }, [visible, isLoadingDefault, minBreakMinutes, maxBreakMinutes]);

  const handleConfirmFinishLecture = async () => {
    if (!selectedLecture) return;

    const duration = getActualDurationMinutes(selectedLecture);
    const requiredMin = Math.min(plannedBreakMinutes, duration);

    if (
      !Number.isInteger(lectureBreakMinutes) ||
      lectureBreakMinutes < requiredMin ||
      lectureBreakMinutes > duration
    ) {
      toast.warn(
        duration < plannedBreakMinutes
          ? `משך ההרצאה בפועל הוא ${duration} דק׳ — יש להזין ${duration} דקות הפסקה (לא יותר ממשך ההרצאה)`
          : `יש להזין לפחות ${requiredMin} דקות הפסקה (לפי התכנון) ועד ${duration} דק׳ (משך ההרצאה בפועל)`,
      );
      return;
    }

    setIsLoading(true);
    try {
      await finishLectureOperation(
        selectedLecture.programId,
        selectedLecture.courseId,
        selectedLecture.id,
        lectureBreakMinutes,
      );
      onHide();
    } finally {
      setIsLoading(false);
    }
  };

  const dialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        disabled={isLoading}
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={handleConfirmFinishLecture}
        className="p-button-danger"
        loading={isLoading}
        disabled={isLoading || isLoadingDefault}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="סיום הרצאה"
      visible={visible}
      style={{ width: '500px' }}
      footer={dialogFooter}
      onHide={() => {
        if (!isLoading) {
          onHide();
        }
      }}
      closable={!isLoading}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <p
          style={{
            fontSize: '1rem',
            color: '#495057',
            fontWeight: 'bold',
          }}
        >
          הינך עומד לסגור את ההרצאה, האם אתה בטוח שברצונך להמשיך?
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <label
            htmlFor="professor-finish-break-minutes"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#4b5563',
            }}
          >
            דקות הפסקה
          </label>
          <InputNumber
            inputId="professor-finish-break-minutes"
            value={lectureBreakMinutes}
            onChange={(e) => {
              const next =
                e.value === null || e.value === undefined
                  ? minBreakMinutes
                  : e.value;
              if (next < minBreakMinutes) {
                setLectureBreakMinutes(minBreakMinutes);
                return;
              }
              if (next > maxBreakMinutes) {
                setLectureBreakMinutes(maxBreakMinutes);
                return;
              }
              setLectureBreakMinutes(next);
            }}
            min={minBreakMinutes}
            max={maxBreakMinutes}
            showButtons
            useGrouping={false}
            disabled={isLoadingDefault || isLoading}
            inputStyle={{
              textAlign: 'right',
              width: '100%',
              boxSizing: 'border-box',
            }}
            style={{
              width: '100%',
              maxWidth: '220px',
              direction: 'ltr',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            backgroundColor: '#fef3cd',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #ffc107',
          }}
        >
          <i
            className="pi pi-info-circle"
            style={{ fontSize: '1.5rem', color: '#856404', marginTop: '2px' }}
          />
          <p
            style={{
              margin: 0,
              fontSize: '0.95rem',
              color: '#856404',
              lineHeight: '1.5',
            }}
          >
            שים לב, במהלך ההרצאה יש להזין נוכחות ואת נושא המפגש. לא ניתן יהיה
            לסגור הרצאה ללא דיווח נוכחות ו/או שם המפגש.
          </p>
        </div>
      </div>
    </Dialog>
  );
};
